import React, { useState } from 'react';
import { X, Loader2, MonitorSmartphone, RefreshCw, WifiOff, AlertTriangle } from 'lucide-react';
import usePosPaymentRequest from './usePosPaymentRequest';

// Pay on the Square Terminal — the sale is pushed to the device, the amount
// appears on its own screen, and the customer taps or inserts their card there.
// The confirmed payment returns by itself; nothing is keyed back into the POS.

export default function WalkInTerminalPayment({ payload, basketKey, devices = [], onPaid, onCancel }) {
  const [deviceId, setDeviceId] = useState('');
  const { request, status, error, cancel, refresh } = usePosPaymentRequest({
    functionName: 'posTerminalPayment',
    payload,
    basketKey,
    onPaid,
    onCancel,
  });

  const amount = Number(request?.amount ?? payload.amount) || 0;
  const label = request?.label || payload.experienceName || payload.experienceType || 'Venue sale';
  const activeDeviceId = deviceId || request?.deviceId || '';
  const deviceName = devices.find((d) => d.id === activeDeviceId)?.name
    || request?.deviceName
    || 'the terminal';
  const expired = status === 'expired';
  const waiting = status === 'pending';

  const switchTo = (id) => {
    if (id === activeDeviceId) return;
    setDeviceId(id);
    refresh({ deviceId: id });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="bg-[#111115] border border-white/10 w-full max-w-lg my-auto">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-white/10">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest">PAY ON TERMINAL</p>
            <p className="text-white font-black text-4xl mt-1">£{amount.toFixed(2)}</p>
            <p className="text-white/40 text-xs mt-1">{label}</p>
          </div>
          <button onClick={cancel} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {status === 'starting' && (
            <p className="flex items-center justify-center gap-2 text-white/40 text-sm py-12">
              <Loader2 className="w-4 h-4 animate-spin" /> Sending the sale to the terminal…
            </p>
          )}

          {waiting && (
            <div className="border border-white/10 bg-[#0d0d0f] px-4 py-8 text-center">
              <MonitorSmartphone className="w-8 h-8 text-primary mx-auto mb-3" />
              <p className="text-white font-black text-lg uppercase">{deviceName}</p>
              <p className="text-white/50 text-sm mt-2 leading-relaxed">
                £{amount.toFixed(2)} has been sent to the terminal. Ask the customer to tap or insert
                their card there — the terminal prints their receipt.
              </p>
              <p className="text-white/35 text-xs mt-3 leading-relaxed">
                The terminal can take a few seconds to wake up and show the amount — there is no need
                to send it again.
              </p>
              <p className="flex items-center justify-center gap-2 text-white/40 text-xs mt-4">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Waiting for the customer to pay
              </p>
            </div>
          )}

          {expired && (
            <div className="border border-amber-500/40 bg-amber-500/10 px-4 py-6 text-center">
              <AlertTriangle className="w-6 h-6 text-amber-300 mx-auto mb-2" />
              <p className="text-amber-300 font-bold text-sm">The terminal request expired</p>
              <p className="text-amber-300/70 text-xs mt-1">
                It has been withdrawn. The basket is untouched — take payment another way, or try again.
              </p>
              <button
                onClick={() => refresh()}
                className="mt-4 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-black text-xs tracking-widest px-6 py-3.5 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" /> SEND AGAIN
              </button>
            </div>
          )}

          {status === 'failed' && (
            <div className="border border-red-500/40 bg-red-500/10 px-4 py-4">
              <p className="text-red-400 font-bold text-sm">
                {error || 'The sale could not be sent to the terminal.'}
              </p>
              <button
                onClick={() => refresh()}
                className="mt-3 inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white/70 hover:text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" /> TRY AGAIN
              </button>
            </div>
          )}

          {/* More than one Terminal paired — staff can move the sale to another
              device for this basket without leaving the payment step. */}
          {devices.length > 1 && (
            <div>
              <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
                Send to
              </p>
              <div className="flex flex-wrap gap-2">
                {devices.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => switchTo(d.id)}
                    className={`flex items-center gap-2 border px-3 py-2 text-xs font-bold transition-colors ${
                      d.id === activeDeviceId
                        ? 'border-primary text-white'
                        : 'border-white/10 text-white/50 hover:text-white hover:border-white/30'
                    }`}
                  >
                    {d.offline && <WifiOff className="w-3 h-3 text-amber-400" />}
                    {d.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={cancel}
            className="w-full border border-white/15 hover:border-white/35 text-white/60 hover:text-white font-bold text-xs tracking-widest py-4 transition-colors"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}